import { PublicLayout } from '@/layouts/PublicLayout';
import { Button } from '@/components/Button';
import { useNavigate } from 'react-router-dom';
import { Check } from 'lucide-react';

export default function PricingPage() {
  const navigate = useNavigate();

  const plans = [
    {
      name: 'Starter',
      price: '99',
      description: 'Perfect for small teams and startups',
      features: [
        'Up to 10 active jobs',
        '100 candidates per month',
        'AI resume screening',
        'Basic analytics',
        'Email support',
        'GDPR compliance tools',
      ],
      cta: 'Start Free Trial',
      popular: false,
    },
    {
      name: 'Professional',
      price: '299',
      description: 'For growing companies with higher volume',
      features: [
        'Up to 50 active jobs',
        '500 candidates per month',
        'Advanced AI matching',
        'Bias detection & audits',
        'Custom workflows',
        'Priority support',
        'API access',
        'Team collaboration',
      ],
      cta: 'Start Free Trial',
      popular: true,
    },
    {
      name: 'Enterprise',
      price: 'Custom',
      description: 'For large organizations with complex needs',
      features: [
        'Unlimited jobs',
        'Unlimited candidates',
        'Dedicated account manager',
        'Custom integrations',
        'SSO & advanced security',
        'SLA guarantees',
        'Custom bias audits',
        'White-label options',
        'Training & onboarding',
      ],
      cta: 'Contact Sales',
      popular: false,
    },
  ];

  const faqs = [
    {
      question: 'Is there a free trial?',
      answer: 'Yes! All plans come with a 14-day free trial. No credit card required.',
    },
    {
      question: 'Can I cancel anytime?',
      answer: 'Absolutely. You can cancel your subscription at any time with no penalties.',
    },
    {
      question: 'What happens after the trial?',
      answer: 'After your trial ends, you\'ll be prompted to select a plan. Your data remains safe either way.',
    },
    {
      question: 'Do you offer discounts for nonprofits?',
      answer: 'Yes! We offer special pricing for nonprofits and educational institutions. Contact us for details.',
    },
    {
      question: 'What payment methods do you accept?',
      answer: 'We accept all major credit cards, ACH transfers, and can invoice for annual plans.',
    },
    {
      question: 'Is implementation included?',
      answer: 'Professional plans include guided onboarding. Enterprise plans include full implementation support.',
    },
  ];

  return (
    <PublicLayout>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-50 via-white to-secondary-50 py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Simple, Transparent Pricing
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-6">
            Choose the plan that fits your needs. All plans include our core Glass Box AI features.
          </p>
          <p className="text-sm text-gray-500">
            14-day free trial · No credit card required · Cancel anytime
          </p>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-24 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-8 lg:grid-cols-3">
            {plans.map((plan) => (
              <div
                key={plan.name}
                className={`relative bg-white rounded-2xl ${
                  plan.popular
                    ? 'ring-2 ring-primary-600 shadow-xl scale-105'
                    : 'border border-gray-200 shadow-sm'
                } p-8`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="inline-flex items-center rounded-full bg-primary-600 px-4 py-1 text-sm font-medium text-white">
                      Most Popular
                    </span>
                  </div>
                )}
                <div className="mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                  <p className="text-gray-600 mb-4">{plan.description}</p>
                  <div className="flex items-baseline">
                    {plan.price !== 'Custom' ? (
                      <>
                        <span className="text-4xl font-bold text-gray-900">${plan.price}</span>
                        <span className="ml-2 text-gray-600">/month</span>
                      </>
                    ) : (
                      <span className="text-4xl font-bold text-gray-900">{plan.price}</span>
                    )}
                  </div>
                </div>
                <ul className="space-y-4 mb-8">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start">
                      <Check className="h-5 w-5 text-primary-600 mr-3 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  className="w-full"
                  variant={plan.popular ? 'primary' : 'outline'}
                  onClick={() => navigate(plan.price === 'Custom' ? '/contact' : '/signup')}
                >
                  {plan.cta}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Comparison */}
      <section className="py-24 bg-gray-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            All Plans Include
          </h2>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[
              'Glass Box AI explanations',
              'Automated bias detection',
              'GDPR compliance tools',
              'Resume parsing',
              'Candidate tracking',
              'Interview scheduling',
              'Mobile access',
              'Secure data storage',
              'Regular updates',
            ].map((feature) => (
              <div key={feature} className="flex items-center bg-white rounded-lg p-4 shadow-sm">
                <Check className="h-5 w-5 text-primary-600 mr-3 flex-shrink-0" />
                <span className="text-gray-700">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-24 bg-white">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Frequently Asked Questions
          </h2>
          <div className="space-y-8">
            {faqs.map((faq) => (
              <div key={faq.question}>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{faq.question}</h3>
                <p className="text-gray-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-primary-600 to-secondary-600">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Still Have Questions?
          </h2>
          <p className="text-xl text-primary-100 mb-10">
            Our team is here to help you find the perfect plan for your needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" variant="secondary" onClick={() => navigate('/contact')}>
              Contact Sales
            </Button>
            <Button size="lg" variant="outline" className="!text-white !border-white hover:!bg-white/10" onClick={() => navigate('/signup')}>
              Start Free Trial
            </Button>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
